/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import Media from 'react-media';
import { object } from 'prop-types';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import mbpLogger from 'mbp-logger';
import { useQuery, useQueryClient } from 'react-query';

// Styles
import { RecipientBookStyle } from '../../Common/Assets/Style/RecipientBookStyle';

// MUI
import { withStyles } from '@material-ui/core';

// Helpers
import CUSTOM_BREAKPOINTS from '../../../../../style/customBreakpoints';
import { RecipientBookContextProvider } from '../../Common/Context/RecipientBookContext';
import { deepObjectExtend } from '../../../../../utils/object';
import { combineStyle } from '../../../../../style/combineStyle';

// API
import { fetchSingleRecipientDetails } from '../../../../../../../../apis/account-apis/RecipientBook-apis/fetchSingleRecipientDetails';
import fetchRecipientBookListWithOccasions from '../../../../../../../../apis/account-apis/RecipientBook-apis/fetchRecipientBookListWithOccasions';

// Component
import EditRecipient from './EditRecipient';
import DesktopEditRecipient from './DesktopEditRecipient';
import Loading from '../../../../../common/Loading';


const styles = ({ typography, palette }) => deepObjectExtend(
    combineStyle(typography, palette, {
        RecipientBookStyle,
    }),
);

const EditRecipientContainer = ({ classes }) => {
    const breakpointExpr = `(max-width: ${(CUSTOM_BREAKPOINTS.tabLandscape)}px)`;
    const userProfile = useSelector((state) => state.account.userProfile || state.member.profile);
    const contactId = userProfile?.contactId || userProfile?.ContactId;
    const history = useHistory();
    const queryClient = useQueryClient();
    let { rcpId, occasionId } = useParams();

    if (!rcpId.includes('ssp')) {
        history.push('/account/address-book');
        return null;
    };

    const { isLoading, data, status, error } = useQuery(
        ["recipientBookList", contactId, rcpId],
        fetchSingleRecipientDetails,
        {
            refetchOnWindowFocus: false,
            retry: false,
            enabled: !!contactId,
        },
    );

    let addressBookResponse = queryClient.getQueryData(['recipientBookList', contactId])?.data?.addressBookResponse;
    useQuery(
        ["recipientBookList", contactId],
        fetchRecipientBookListWithOccasions,
        {
            refetchOnWindowFocus: false,
            retry: false,
            enabled: !!addressBookResponse?.addressList || !!contactId,
        },
    );

    if (data?.data && addressBookResponse?.addressList?.length > 0 && data?.data?.addressBookResponse?.addressList?.[0]?.isSelfRecipientAvailable === undefined) {
        const addressList = addressBookResponse?.addressList;
        data.data.addressBookResponse.addressList[0].isSelfRecipientAvailable = addressList.find(address => +address.relationship === 0) ? true : false
    }

    if (isLoading) {
        return <Loading />;
    }

    if (status === 'error') {
        let recipientBookListResError = 'GENERIC_API_ERROR';
        if (error.message === 'Request failed with status code 404') {
            recipientBookListResError = 'NO_RECIPIENT_FOUND';
        }
        mbpLogger.logError({
            function: 'getRecipient',
            appName: process.env.npm_package_name,
            module: 'EditRecipientContainer',
            message: `Error While fetching recipient for contactId ${userProfile?.ContactId}`,
            error,
        });
        return (
            <>
                <Media query={breakpointExpr}>
                    {(matches) => (
                        matches ? (
                            <RecipientBookContextProvider>
                                <EditRecipient
                                    classes={classes}
                                    recipient={{}}
                                    recipientBookListResError={recipientBookListResError}
                                />
                            </RecipientBookContextProvider>)
                            : (
                            <RecipientBookContextProvider>
                                <DesktopEditRecipient
                                    classes={classes}
                                    recipient={{}}
                                    recipientBookListResError={recipientBookListResError}
                                />
                            </RecipientBookContextProvider>)
                    )}
                </Media>
            </>
        );
    };

    return (
        <>
            <Media query={breakpointExpr}>
                {(matches) => (
                    matches ? (
                        <RecipientBookContextProvider>
                            <EditRecipient
                                classes={classes}
                                recipient={data.data}
                                selectedOccasionId={occasionId}
                            />
                        </RecipientBookContextProvider>)
                        : (
                        <RecipientBookContextProvider>
                            <DesktopEditRecipient
                                classes={classes}
                                recipient={data.data}
                                selectedOccasionId={occasionId}
                            />
                        </RecipientBookContextProvider>)
                )}
            </Media>
        </>
    )
}

EditRecipientContainer.propTypes = {
    classes: object.isRequired,
};

export default withStyles(styles)(EditRecipientContainer);
